<template>
  <div
    class="flex flex-auto flex-col bg-bg-base-bg1 rounded-t-12"
  >
    <div
      class="px-4 py-2"
    >
      <wui-input
        v-bind="$attrs"
        v-model="search"
        type="text"
        data-test="participants-search"
        :placeholder="$t('common.participantSearch')"
        with-clear
        :prepend-icon="isLoading ? 'spinner' : 'searchM'"
      />
    </div>
    <div
      class="grow px-4"
    >
      <template v-if="internalModerators.length && isModerated">
        <div class="grid grid-rows-1 items-end">
          <p class="text-1316 text-primary-tints-800">
            {{ $t('common.willBeAddedToChatAsModerators') }}
          </p>
          <hr
            class="mt-2 border-t border-bor-nav"
          >
        </div>
        <p
          v-if="!internalModerators.length"
          class="pt-2 text-1316"
        >
          {{ $t('common.rightClickAddModerator') }}
        </p>
      </template>
      <selected-contacts-list
        class="pt-2"
        :contacts="internalModerators"
        role="Moderator"
        :is-moderated="isModerated!"
        @remove="onRemoveModerator"
        @toggle-role="(number: string) => toggleRole(number, 'Moderator')"
      />
    </div>
    <div
      v-if="internalSubscribers.length"
      class="px-4"
    >
      <div class="flex flex-col short:pt-2">
        <p
          class="text-1316 text-primary-tints-800"
        >
          {{ $t('common.willBeAddedToChat') }}
        </p>
        <hr
          class="mt-2 border-t border-bor-nav"
        >
      </div>
      <selected-contacts-list
        :contacts="internalSubscribers"
        role="User"
        :is-moderated="isModerated!"
        @remove="onRemoveSubscriber"
        @toggle-role="(number: string) => toggleRole(number, 'User')"
      />
    </div>
    <div
      class="relative h-full overflow-y-auto will-change-scroll"
    >
      <list-plug
        v-if="isSearchEmpty"
        :text="$t('common.nothingFound')"
        icon="userM"
      />
      <virtual-contact-list
        v-else
        class="absolute w-full grow"
        :contacts="remainingContacts"
        :total="remainingContacts.length"
        :show-external-contact-modal="false"
        :with-navigation="false"
        :is-moderated="isModerated!"
        :is-for-adding-to-chat="true"
        favorites-first
        @get-contact="onAddSubscriber"
        @remove-from-selected="onRemoveSubscriber"
        @on-add-moderator="onAddModerator"
        @on-remove-moderator="onRemoveModerator"
        @on-add-subscriber="onAddSubscriber"
        @on-remove-subscriber="onRemoveSubscriber"
        @scroll="onScroll"
      />
    </div>
  </div>
</template>
<script lang="ts" setup>
import { WuiInput } from '@wui/common-library'
import { ConferenceMember, ConferenceMemberRole } from '@wui/im'
import debounce from 'lodash.debounce'
import { storeToRefs } from 'pinia'
import { computed, nextTick, ref, watch, onBeforeMount } from 'vue'

import { SelectedContactsList, useStoreContacts, VirtualContactList } from '@/entities/contact'

import { ListPlug } from '@/shared/components/list-plug'
import { useSearch } from '@/shared/composables/search-handlers'
import { Contact } from '@/shared/types/api'
import sort from '@/shared/utils/sort'
import { searchContact } from '@/shared/utils/utils'

const props = defineProps<{
  modelValue: Array<ConferenceMember>,
  isModerated?: boolean
}>()

const emits = defineEmits<{
  (e: 'update:modelValue', modelValue: Array<ConferenceMember>): void
  (e: 'toggle-role', internalNumber: string, role: ConferenceMemberRole): void
}>()

const storeContacts = useStoreContacts()
const { alphabeticallySortedContacts, allContacts } = storeToRefs(storeContacts)
const contacts = computed<Contact[]>((): Contact[] => search.value ? allContacts.value : alphabeticallySortedContacts.value)
const { search, filtered } = useSearch(contacts, searchContact)

const selectedSubscriberIds = ref<string[]>([])
const selectedModeratorIds = ref<string[]>([])
let skipModelValueSync = false

const getSubscriberNumber = (contact: Contact) =>
  contact.mobilePhone && !contact.imLogin ? contact.mobilePhone : contact.internalNumber

const getSubscriberRole = (contact: Contact): 'User' | 'External' =>
  contact.imLogin ? 'User' : 'External'

const contactToConferenceMember = (contact: Contact): ConferenceMember => ({
  role: getSubscriberRole(contact),
  number: getSubscriberNumber(contact),
})

const resolveContactsFromStore = (ids: string[]): Contact[] =>
  ids
    .map(id => allContacts.value.find(c => c.id === id))
    .filter((c): c is Contact => c !== undefined)

const internalSubscribers = computed(() => resolveContactsFromStore(selectedSubscriberIds.value))
const internalModerators = computed(() => resolveContactsFromStore(selectedModeratorIds.value))

const chosenSubscribers = computed<ConferenceMember[]>(() => [
  ...internalSubscribers.value.map(contactToConferenceMember),
  ...internalModerators.value.map(m => ({
    role: 'Moderator' as const,
    number: m.internalNumber,
  })),
])

const selectedSubscriberIdsSet = computed(() => new Set(selectedSubscriberIds.value))
const selectedModeratorIdsSet = computed(() => new Set(selectedModeratorIds.value))

const remainingContacts = computed(() => {
  const remaining = filtered.value.filter(item =>
    !selectedSubscriberIdsSet.value.has(item.id) && !selectedModeratorIdsSet.value.has(item.id),
  )

  if (!search.value) {
    return remaining
  }

  return [...remaining].sort(sort.contacts.byFavoriteThenName)
})

const isLoading = ref(false)

const isSearchEmpty = computed(() =>
  Boolean(search.value) && !filtered.value.length && !isLoading.value,
)

const toggleRole = (internalNumber: string, role: ConferenceMemberRole) => {
  emits('toggle-role', internalNumber, role)
}

const emitModelValue = () => {
  skipModelValueSync = true
  emits('update:modelValue', chosenSubscribers.value)
  nextTick(() => {
    skipModelValueSync = false
  })
}

const onRemoveSubscriber = (id: string) => {
  selectedSubscriberIds.value = selectedSubscriberIds.value.filter(contactId => contactId !== id)
  emitModelValue()
}

const onAddSubscriber = (contact: Contact) => {
  if (selectedSubscriberIdsSet.value.has(contact.id)) {
    return
  }

  selectedSubscriberIds.value = [...selectedSubscriberIds.value, contact.id]
  emitModelValue()
}

const onAddModerator = (contact: Contact) => {
  if (selectedModeratorIdsSet.value.has(contact.id)) {
    return
  }

  selectedModeratorIds.value = [...selectedModeratorIds.value, contact.id]
  emitModelValue()
}

const onRemoveModerator = (id: string) => {
  selectedModeratorIds.value = selectedModeratorIds.value.filter(contactId => contactId !== id)
  emitModelValue()
}

const syncSelectedIdsFromModelValue = () => {
  const subscriberIds: string[] = []
  const usedSubscriberIds = new Set<string>()

  for (const member of props.modelValue) {
    if (member.role === 'Moderator') {
      continue
    }

    if (member.role !== 'User' && member.role !== 'External') {
      continue
    }

    const matching = allContacts.value.filter(c =>
      !usedSubscriberIds.has(c.id)
      && getSubscriberRole(c) === member.role
      && getSubscriberNumber(c) === member.number,
    )

    const preserved = selectedSubscriberIds.value.find(id =>
      matching.some(c => c.id === id),
    )
    const contactId = preserved ?? matching[0]?.id

    if (contactId) {
      subscriberIds.push(contactId)
      usedSubscriberIds.add(contactId)
    }
  }

  selectedSubscriberIds.value = subscriberIds
  selectedModeratorIds.value = props.modelValue
    .filter(m => m.role === 'Moderator')
    .map(m => allContacts.value.find(c => c.internalNumber === m.number)?.id)
    .filter((id): id is string => id !== undefined)
}

const onScroll = () => {
  if (storeContacts.isLoading) {
    return
  }

  storeContacts.fetchNext()
}

onBeforeMount(() => {
  syncSelectedIdsFromModelValue()
})

watch(() => props.modelValue, () => {
  if (skipModelValueSync) {
    return
  }

  syncSelectedIdsFromModelValue()
}, {
  deep: true,
})

watch(search, debounce(async (v) => {
  if (v) {
    try {
      isLoading.value = true
      await storeContacts.fetchContactsByNameOrNumberIfListIsNotLoaded(v)
    } catch (e) {
      console.error(e)
    } finally {
      isLoading.value = false
    }
  }
}, 2000))
</script>
